import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import {
  approveTopic,
  fetchProducttypeFunnels,
  fetchProducttypeStatistics,
  fetchTopics,
  ignoreTopic,
  resetTopic,
} from '@/api/topics/api'
import type {
  ProducttypeFunnelPage,
  ProducttypeFunnelQuery,
  ProducttypeStatistics,
  ProducttypeStatisticsQuery,
  TopicDecision,
  TopicPage,
  TopicQuery,
} from '@/api/topics/types'

const TOPICS_KEY = 'topics' as const
const FUNNELS_KEY = 'producttype-funnels' as const
const STATISTICS_KEY = 'producttype-statistics' as const

export function useTopics(query: TopicQuery, enabled = true): UseQueryResult<TopicPage> {
  return useQuery({
    queryKey: [TOPICS_KEY, query],
    queryFn: ({ signal }) => fetchTopics(query, signal),
    /*
     * Die Liste ist ein Arbeitsvorrat, keine Live-Anzeige. Ein Neuladen
     * beim Fensterwechsel würde mitten in der Sichtung die Reihenfolge
     * unter dem Cursor verändern.
     */
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    enabled,
  })
}

/** Die paginierten, serverseitig aggregierten Produkttyp-Hauptrichter. */
export function useProducttypeFunnels(
  query: ProducttypeFunnelQuery,
): UseQueryResult<ProducttypeFunnelPage> {
  return useQuery({
    queryKey: [FUNNELS_KEY, query],
    queryFn: ({ signal }) => fetchProducttypeFunnels(query, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

/** Der serverseitig berechnete Statistiküberblick über die gesamte Treffermenge. */
export function useProducttypeStatistics(
  query: ProducttypeStatisticsQuery,
): UseQueryResult<ProducttypeStatistics> {
  return useQuery({
    queryKey: [STATISTICS_KEY, query],
    queryFn: ({ signal }) => fetchProducttypeStatistics(query, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

interface TopicActions {
  readonly approve: (topicId: number) => void
  readonly ignore: (topicId: number) => void
  readonly reset: (topicId: number) => void
  readonly pendingTopicId: number | null
  readonly lastDecision: TopicDecision | null
  readonly error: Error | null
}

/**
 * Die drei Entscheidungen zu einem Thema.
 *
 * Nach jeder Änderung wird die Liste neu geladen statt lokal gepatcht.
 * Beim Freigeben entsteht im Backend eine Nische, deren Daten wir nicht
 * raten wollen, und ein falsch geratener Zustand wäre hier schlimmer
 * als ein kurzer Ladevorgang.
 */
export function useTopicActions(): TopicActions {
  const queryClient = useQueryClient()

  /*
   * Beide Zwischenspeicher verwerfen, nicht nur den der Liste. Sonst
   * zeigt der Überblick nach einer Freigabe weiter den alten Stand,
   * und zwar genau die Kachel, die den Fortschritt zählt.
   */
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [TOPICS_KEY] }),
      queryClient.invalidateQueries({ queryKey: [FUNNELS_KEY] }),
      queryClient.invalidateQueries({ queryKey: [STATISTICS_KEY] }),
    ])
  }

  const approve = useMutation<TopicDecision, Error, number>({
    mutationFn: approveTopic,
    onSuccess: invalidate,
  })

  const ignore = useMutation<TopicDecision, Error, number>({
    mutationFn: ignoreTopic,
    onSuccess: invalidate,
  })

  const reset = useMutation<TopicDecision, Error, number>({
    mutationFn: resetTopic,
    onSuccess: invalidate,
  })

  const pendingTopicId = approve.variables ?? ignore.variables ?? reset.variables ?? null

  const isPending = approve.isPending || ignore.isPending || reset.isPending

  /*
   * Die jüngste Entscheidung, egal welche der drei es war. Sie trägt die
   * einzige Information, die nicht in der neu geladenen Liste steht:
   * ob eine Nische neu entstanden ist oder verwaist zurückbleibt.
   */
  const lastDecision =
    [approve, ignore, reset]
      .filter((mutation) => mutation.data !== undefined)
      .sort((a, b) => b.submittedAt - a.submittedAt)[0]?.data ?? null

  return {
    approve: (topicId) => {
      approve.mutate(topicId)
    },
    ignore: (topicId) => {
      ignore.mutate(topicId)
    },
    reset: (topicId) => {
      reset.mutate(topicId)
    },
    pendingTopicId: isPending ? pendingTopicId : null,
    lastDecision,
    error: approve.error ?? ignore.error ?? reset.error,
  }
}
